/**
 * DeleteEntryModal - Modal per eliminare entry con motivo obbligatorio
 * Il Diario di Bordo - SaaS
 * 
 * Uso:
 * <DeleteEntryModal
 *   entry={entryToDelete}
 *   onClose={() => setEntryToDelete(null)}
 *   onConfirm={handleDelete}
 *   loading={deleting}
 * />
 */

import { useState } from 'react'
import { Trash2, AlertTriangle, X } from 'lucide-react'

export function DeleteEntryModal({ entry, onClose, onConfirm, loading = false }) {
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  
  if (!entry) return null
  
  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!reason.trim()) {
      setError('Il motivo è obbligatorio')
      return
    }
    
    if (reason.trim().length < 3) {
      setError('Il motivo deve essere di almeno 3 caratteri')
      return
    }
    
    setError('')
    onConfirm(entry._id, reason.trim())
  }
  
  const formatDate = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-500" />
            Sposta nel Cestino
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        
        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Info entry */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Stai per spostare nel cestino questa registrazione:
            </p>
            <div className="mt-2 space-y-1">
              <p className="font-medium text-gray-900 dark:text-white">
                👤 {entry.minore?.nomeCompleto || entry.minoreNome || 'N/D'}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                📅 {formatDate(entry.data)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                👨‍🏫 {entry.educatore}
              </p>
            </div>
          </div>
          
          {/* Campo motivo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Motivo eliminazione <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value)
                if (error) setError('')
              }}
              placeholder="Es: Registrazione duplicata, inserita per errore..."
              rows={3}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white resize-none ${
                error 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              autoFocus
            />
            {error && (
              <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {error}
              </p>
            )}
          </div>
          
          {/* Info retention */}
          <div className="flex items-start gap-2 text-sm text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <span className="text-blue-500">ℹ️</span>
            <p>
              La registrazione resterà nel cestino per <strong>15 giorni</strong>, 
              dopodiché verrà eliminata definitivamente. 
              Potrai ripristinarla in qualsiasi momento dal Cestino.
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading || !reason.trim()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Eliminazione...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Sposta nel Cestino
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
